"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HIGHLIGHT_TONES,
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type DoiFlag,
  type DoiStyle,
  doiListHtml,
  doiUrl,
  type FinalStop,
  formatDoiList,
} from "@/lib/tool-kit/identifiers/doi-text";

/** English strings. A translated content file overrides these through `ui`. */
export const DOI_LINK_FORMATTER_STRINGS = {
  inputLabel: "Your reference list",
  inputPlaceholder:
    "Paste references in any style, one per line. Every DOI is found, however it is written.",
  inputHint:
    "Runs entirely in your browser — nothing you paste is sent anywhere.",
  trySample: "Try a sample",
  clear: "Clear",
  sample:
    "Aspelmeyer, M. (2009). Measured measurement. Nature Physics, 5(1), 11–12. doi:10.1038/nphys1170\nLight, J., & McNaughton, D. (2014). From basic to applied research to improve outcomes for individuals who require augmentative and alternative communication: Potential contributions of eye tracking research methods. Augmentative and Alternative Communication, 30(2), 99–105. Retrieved from http://dx.doi.org/10.3109/07434618.2014.906498\nNewcombe, R. G. (1998). Two-sided confidence intervals for the single proportion: Comparison of seven methods. Statistics in Medicine, 17(8), 857–872. https://doi.org/10.1002/(SICI)1097-0258(19980430)17:8<857::AID-SIM777>3.0.CO;2-E\nRay, O. (2004). How the mind hurts and heals the body. American Psychologist, 59(1), 29–40. DOI: 10.1037/0003-066X.59.1.29.",
  formLabel: "Write each DOI as",
  formHttps: "https://doi.org/10.… (APA 7, MLA 9, Chicago)",
  formAma: "doi:10.… (AMA)",
  formIeee: "doi: 10.… (IEEE)",
  formBare: "10.… (the DOI alone)",
  dropRetrieved: "Remove “Retrieved from” before a DOI (left over from APA 6)",
  stopLabel: "Full stop after a DOI that ends a reference",
  stopKeep: "Leave it as written",
  stopRemove: "Remove it (APA 7, AMA)",
  stopAdd: "Add one (IEEE, MLA 9, Chicago)",
  emptyPrompt: "Paste a reference list to find and rewrite its DOIs.",
  noneFound:
    "No DOIs found. A DOI starts with 10., then a registrant code of four or more digits, a slash and a suffix.",
  results: "Cleaned list",
  foundCount: "one:{count} DOI found|other:{count} DOIs found",
  changedCount: "one:{count} rewritten|other:{count} rewritten",
  copyText: "Copy as plain text",
  copyLinks: "Copy with live links",
  copied: "Copied!",
  previewLabel: "Your list with every DOI rewritten",
  legendChanged: "Rewritten",
  legendFlagged: "Needs checking",
  changesHeading: "Every change",
  colLine: "Line",
  colBefore: "Before",
  colAfter: "After",
  noChanges: "Every DOI is already in this form — nothing to change.",
  flagsHeading: "Needs checking",
  flagLine: "Line {line}: {written}",
  flagNotADoi:
    "Not a DOI. A DOI starts with 10. and a registrant code of four or more digits — left as it is.",
  flagNoSuffix:
    "Nothing follows the slash, so the DOI may have been cut off — left as it is.",
  flagCharacter:
    "Contains {characters}, a look-alike or invisible character a PDF can leave behind. The link will probably not resolve until you retype it.",
  flagSplit:
    "Ends in a hyphen at the end of the line, so it probably continues on the next line. Join the two lines.",
  flagInLink:
    "A DOI inside a publisher's link — left as it is. APA 7 asks for the DOI link instead: {link}",
  characterCode: "“{char}” (U+{code})",
} as const;

type Strings = typeof DOI_LINK_FORMATTER_STRINGS;

const TONES = { changed: "emerald", flagged: "amber" } as const;

function characterList(characters: string[], strings: Strings): string {
  return characters
    .map((char) =>
      t(strings.characterCode, {
        char,
        code: (char.codePointAt(0) ?? 0)
          .toString(16)
          .toUpperCase()
          .padStart(4, "0"),
      })
    )
    .join(", ");
}

function flagMessages(flag: DoiFlag, strings: Strings): string[] {
  const messages: string[] = [];
  if (flag.inLink && flag.doi) {
    messages.push(t(strings.flagInLink, { link: doiUrl(flag.doi) }));
  }
  for (const problem of flag.problems) {
    if (problem === "not-a-doi") {
      messages.push(strings.flagNotADoi);
    } else if (problem === "no-suffix") {
      messages.push(strings.flagNoSuffix);
    } else if (problem === "split-line") {
      messages.push(strings.flagSplit);
    } else {
      messages.push(
        t(strings.flagCharacter, {
          characters: characterList(flag.characters, strings),
        })
      );
    }
  }
  return messages;
}

function FlagList({ flags, strings }: { flags: DoiFlag[]; strings: Strings }) {
  const fmt = useToolNumber();
  return (
    <ul className="space-y-2 text-sm">
      {flags.map((flag) => (
        <li
          className="rounded-lg border border-border bg-card p-3"
          key={`${flag.line}|${flag.written}`}
        >
          <span className="break-all font-mono text-foreground">
            {t(strings.flagLine, {
              line: fmt(flag.line),
              written: flag.written,
            })}
          </span>
          {flagMessages(flag, strings).map((message) => (
            <p className="mt-1 text-muted-foreground" key={message}>
              {message}
            </p>
          ))}
        </li>
      ))}
    </ul>
  );
}

export default function DoiLinkFormatterWidget() {
  const strings = useToolUi(DOI_LINK_FORMATTER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [style, setStyle] = useState<DoiStyle>("https");
  const [dropRetrieved, setDropRetrieved] = useState(true);
  const [finalStop, setFinalStop] = useState<FinalStop>("keep");
  const deferred = useDeferredValue(text);

  const result = useMemo(
    () => formatDoiList(deferred, { style, dropRetrieved, finalStop }),
    [deferred, style, dropRetrieved, finalStop]
  );
  const matches: TextMatch[] = result.marks
    .filter((mark) => mark.state !== "unchanged")
    .map((mark) => ({
      start: mark.start,
      end: mark.end,
      category: mark.state,
      title:
        mark.state === "changed"
          ? strings.legendChanged
          : strings.legendFlagged,
    }));
  const found = result.found;
  const hasText = deferred.trim() !== "";

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="dlf-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={8}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text === ""}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <PillGroup
        label={strings.formLabel}
        name="dlf-form"
        onChange={setStyle}
        options={[
          { value: "https", label: strings.formHttps },
          { value: "ama", label: strings.formAma },
          { value: "ieee", label: strings.formIeee },
          { value: "bare", label: strings.formBare },
        ]}
        value={style}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id="dlf-stop"
          label={strings.stopLabel}
          onChange={setFinalStop}
          options={[
            { value: "keep", label: strings.stopKeep },
            { value: "remove", label: strings.stopRemove },
            { value: "add", label: strings.stopAdd },
          ]}
          value={finalStop}
        />
        <div className="flex items-end">
          <Checkbox
            checked={dropRetrieved}
            label={strings.dropRetrieved}
            onChange={setDropRetrieved}
          />
        </div>
      </div>

      <div aria-live="polite" className="space-y-6">
        {hasText ? null : <Notice>{strings.emptyPrompt}</Notice>}
        {hasText && found === 0 ? <Notice>{strings.noneFound}</Notice> : null}
        {hasText && found > 0 ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>{strings.results}</h2>
                <p className="text-muted-foreground text-sm">
                  {tPlural(strings.foundCount, locale, found, {
                    count: fmt(found),
                  })}
                  {" · "}
                  {tPlural(strings.changedCount, locale, result.edits.length, {
                    count: fmt(result.edits.length),
                  })}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <CopyButton
                  copiedLabel={strings.copied}
                  label={strings.copyText}
                  text={result.text}
                />
                <CopyButton
                  copiedLabel={strings.copied}
                  html={doiListHtml(result)}
                  label={strings.copyLinks}
                  text={result.text}
                />
              </div>
            </div>
            <HighlightedText
              label={strings.previewLabel}
              matches={matches}
              text={result.text}
              tones={TONES}
            />
            <p className={HINT}>
              <mark className={HIGHLIGHT_TONES.emerald}>
                {strings.legendChanged}
              </mark>{" "}
              <mark className={HIGHLIGHT_TONES.amber}>
                {strings.legendFlagged}
              </mark>
            </p>
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.changesHeading}</h3>
              {result.edits.length > 0 ? (
                <DataTable
                  head={[strings.colLine, strings.colBefore, strings.colAfter]}
                  rows={result.edits.map((edit) => [
                    fmt(edit.line),
                    edit.before,
                    edit.after,
                  ])}
                />
              ) : (
                <Notice>{strings.noChanges}</Notice>
              )}
            </div>
            {result.flags.length > 0 ? (
              <div className="space-y-3">
                <h3 className={WIDGET_H3}>{strings.flagsHeading}</h3>
                <FlagList flags={result.flags} strings={strings} />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
